// PR-31: side drawer — ChatGPT-style left slide-in.
//
// Carries the bits that desktop puts in the Sidebar: project picker, thread
// list (active project), + NEW thread, archived toggle, user/MCP/sign-out.
// One drawer per shell mount; backdrop closes on tap (or Esc).
//
// PR-31 keeps the surface intentionally narrow:
//   - tap thread row → select + close drawer
//   - tap + NEW → create + select + close drawer
//   - long-press a thread row → action sheet (Rename / Archive)
//   - tap project row → switch + close drawer
// More (search, pin, drag-reorder) can land in later PRs.

import { useEffect, useRef, useState } from 'react'
import type { MeResponse, Project, Thread } from '../../api'

interface MobileSideDrawerProps {
  open: boolean
  onClose: () => void

  projects: Project[]
  activeProject: Project | null
  onSelectProject: (p: Project) => void

  threads: Thread[]
  activeThreadId: string | null
  onSelectThread: (id: string) => void
  onNewThread: () => void
  onRenameThread: (id: string, title: string) => void
  onArchiveThread: (id: string) => void

  me: MeResponse
  onLogout: () => void | Promise<void>

  /** Per-thread inference flags; mirrors desktop. Drives a breathing dot
   *  on each thread row so users can see "this slow turn is still going"
   *  without opening the chat pane. */
  busyByThread?: Record<string, boolean>
  /** Project ids with at least one busy thread (off-screen). Drives a
   *  breathing dot next to off-screen project names in the picker. */
  busyProjects?: ReadonlySet<string>
  /** Phase 2: per-thread unread count. Active thread is always 0. */
  unreadByThread?: Record<string, number>
  /** Phase 2: project ids with any unread thread. */
  unreadProjects?: ReadonlySet<string>
}

/** Mirror of the desktop Sidebar's StatusDot. busy beats unread when both
 *  apply (the live state is more informative — unread will collapse to
 *  "read" the moment the user navigates in). */
function MobileStatusDot({
  busy,
  unread,
}: {
  busy: boolean
  unread: boolean
}) {
  if (!busy && !unread) return null
  return (
    <span
      aria-hidden="true"
      className={busy ? 'mobile-drawer__status-dot mobile-drawer__status-dot--busy' : 'mobile-drawer__status-dot mobile-drawer__status-dot--unread'}
    />
  )
}

const LONG_PRESS_MS = 450

export function MobileSideDrawer({
  open,
  onClose,
  projects,
  activeProject,
  onSelectProject,
  threads,
  activeThreadId,
  onSelectThread,
  onNewThread,
  onRenameThread,
  onArchiveThread,
  me,
  onLogout,
  busyByThread,
  busyProjects,
  unreadByThread,
  unreadProjects,
}: MobileSideDrawerProps) {
  const [archivedExpanded, setArchivedExpanded] = useState(false)
  const [projectListExpanded, setProjectListExpanded] = useState(false)
  const [actionSheetThreadId, setActionSheetThreadId] = useState<string | null>(null)
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameDraft, setRenameDraft] = useState('')

  // Close on Esc — pairs with backdrop tap.
  useEffect(() => {
    if (!open) return
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [open, onClose])

  // Reset transient state when drawer closes so a re-open doesn't pop a
  // half-finished action sheet from the prior session.
  useEffect(() => {
    if (open) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setActionSheetThreadId(null)
    setRenamingId(null)
    setProjectListExpanded(false)
  }, [open])

  // Long-press detection on a thread row. Touch-first; pointerdown also
  // covers desktop dev-tools mobile emulator.
  const longPressTimer = useRef<number | null>(null)
  const longPressFiredRef = useRef(false)
  const startLongPress = (threadId: string) => {
    longPressFiredRef.current = false
    if (longPressTimer.current) window.clearTimeout(longPressTimer.current)
    longPressTimer.current = window.setTimeout(() => {
      longPressFiredRef.current = true
      setActionSheetThreadId(threadId)
    }, LONG_PRESS_MS)
  }
  const cancelLongPress = () => {
    if (longPressTimer.current) {
      window.clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }

  const visibleThreads = threads.filter((t) => !t.archived)
  const archivedThreads = threads.filter((t) => t.archived)
  const actionSheetThread = actionSheetThreadId
    ? threads.find((t) => t.id === actionSheetThreadId) ?? null
    : null

  return (
    <>
      <div
        className={`mobile-drawer-backdrop${open ? ' is-open' : ''}`}
        onClick={onClose}
        aria-hidden={!open}
      />
      <aside
        className={`mobile-drawer${open ? ' is-open' : ''}`}
        aria-label="Navigation drawer"
        aria-hidden={!open}
      >
        {/* Project header — tap to expand list of all projects. */}
        <div className="mobile-drawer__section mobile-drawer__projects">
          <button
            type="button"
            className="mobile-drawer__project-active"
            onClick={() => setProjectListExpanded((v) => !v)}
            aria-expanded={projectListExpanded}
          >
            <span className="mobile-drawer__project-glyph" aria-hidden>●</span>
            <span className="mobile-drawer__project-name">
              {activeProject?.name ?? 'No project'}
            </span>
            <span className="mobile-drawer__chevron" aria-hidden>
              {projectListExpanded ? '▴' : '▾'}
            </span>
          </button>
          {projectListExpanded && (
            <div className="mobile-drawer__project-list">
              {projects.map((p) => {
                const isActive = activeProject?.id === p.id
                const projectBusy = !isActive && (busyProjects?.has(p.id) ?? false)
                const projectUnread = !isActive && (unreadProjects?.has(p.id) ?? false)
                return (
                  <button
                    key={p.id}
                    type="button"
                    className="mobile-drawer__project-row"
                    aria-current={isActive}
                    onClick={() => {
                      setProjectListExpanded(false)
                      if (p.id !== activeProject?.id) {
                        onSelectProject(p)
                        onClose()
                      }
                    }}
                  >
                    <MobileStatusDot busy={projectBusy} unread={projectUnread} />
                    <span className="mobile-drawer__project-row-name">{p.name}</span>
                    {p.pack && (
                      <span className="mobile-drawer__project-row-pack mono">{p.pack}</span>
                    )}
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Threads */}
        <div className="mobile-drawer__section mobile-drawer__threads">
          <div className="mobile-drawer__section-header">
            <span className="mobile-drawer__section-label">THREADS</span>
            <button
              type="button"
              className="mobile-drawer__new"
              onClick={() => {
                onNewThread()
                onClose()
              }}
              disabled={!activeProject}
              aria-label="New thread"
            >
              + NEW
            </button>
          </div>

          {visibleThreads.length === 0 && (
            <div className="mobile-drawer__empty">
              No threads yet — tap + NEW
            </div>
          )}

          <div className="mobile-drawer__thread-list">
            {visibleThreads.map((t) => {
              const isActive = activeThreadId === t.id
              return (
                <ThreadRow
                  key={t.id}
                  thread={t}
                  active={isActive}
                  busy={!!busyByThread?.[t.id]}
                  unread={!isActive && (unreadByThread?.[t.id] ?? 0) > 0}
                  renaming={renamingId === t.id}
                  renameDraft={renameDraft}
                  onRenameDraftChange={setRenameDraft}
                  onCommitRename={() => {
                    const trimmed = renameDraft.trim()
                    if (trimmed && trimmed !== (t.title ?? '')) {
                      onRenameThread(t.id, trimmed)
                    }
                    setRenamingId(null)
                  }}
                  onCancelRename={() => setRenamingId(null)}
                  onTap={() => {
                    if (longPressFiredRef.current) return
                    if (renamingId === t.id) return
                    onSelectThread(t.id)
                    onClose()
                  }}
                  onLongPressStart={() => startLongPress(t.id)}
                  onLongPressEnd={cancelLongPress}
                />
              )
            })}
          </div>

          {archivedThreads.length > 0 && (
            <button
              type="button"
              className="mobile-drawer__archived-toggle"
              onClick={() => setArchivedExpanded((v) => !v)}
            >
              {archivedExpanded ? '▾' : '▸'} Archived ({archivedThreads.length})
            </button>
          )}
          {archivedExpanded && archivedThreads.map((t) => (
            <button
              key={t.id}
              type="button"
              className="mobile-drawer__thread-row mobile-drawer__thread-row--archived"
              onClick={() => {
                onSelectThread(t.id)
                onClose()
              }}
            >
              <span className="mobile-drawer__thread-title">
                {t.title || 'untitled'}
              </span>
            </button>
          ))}
        </div>

        {/* Footer — user + settings hint + sign out */}
        <div className="mobile-drawer__footer">
          <div className="mobile-drawer__user mono" title={me.email ?? me.user_id}>
            {me.email ?? me.display_name ?? me.user_id}
          </div>
          <div className="mobile-drawer__hint">
            ⚙ Settings, MCP, and frame editing live on web desktop.
          </div>
          <button
            type="button"
            className="mobile-drawer__signout"
            onClick={async () => {
              await onLogout()
            }}
          >
            Sign out
          </button>
        </div>
      </aside>

      {actionSheetThread && (
        <div
          className="mobile-action-sheet-backdrop"
          onClick={() => setActionSheetThreadId(null)}
        >
          <div
            className="mobile-action-sheet"
            onClick={(e) => e.stopPropagation()}
            role="menu"
          >
            <div className="mobile-action-sheet__title">
              {actionSheetThread.title || 'untitled'}
            </div>
            <button
              type="button"
              className="mobile-action-sheet__row"
              onClick={() => {
                setRenamingId(actionSheetThread.id)
                setRenameDraft(actionSheetThread.title ?? '')
                setActionSheetThreadId(null)
              }}
            >
              Rename
            </button>
            <button
              type="button"
              className="mobile-action-sheet__row mobile-action-sheet__row--danger"
              onClick={() => {
                onArchiveThread(actionSheetThread.id)
                setActionSheetThreadId(null)
              }}
            >
              Archive
            </button>
            <button
              type="button"
              className="mobile-action-sheet__row mobile-action-sheet__row--cancel"
              onClick={() => setActionSheetThreadId(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  )
}

interface ThreadRowProps {
  thread: Thread
  active: boolean
  busy: boolean
  unread: boolean
  renaming: boolean
  renameDraft: string
  onRenameDraftChange: (v: string) => void
  onCommitRename: () => void
  onCancelRename: () => void
  onTap: () => void
  onLongPressStart: () => void
  onLongPressEnd: () => void
}

function ThreadRow({
  thread,
  active,
  busy,
  unread,
  renaming,
  renameDraft,
  onRenameDraftChange,
  onCommitRename,
  onCancelRename,
  onTap,
  onLongPressStart,
  onLongPressEnd,
}: ThreadRowProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (renaming) inputRef.current?.focus()
  }, [renaming])

  if (renaming) {
    return (
      <div className="mobile-drawer__thread-row mobile-drawer__thread-row--renaming">
        <input
          ref={inputRef}
          type="text"
          className="mobile-drawer__rename-input"
          value={renameDraft}
          onChange={(e) => onRenameDraftChange(e.target.value)}
          onBlur={onCommitRename}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              onCommitRename()
            } else if (e.key === 'Escape') {
              e.preventDefault()
              onCancelRename()
            }
          }}
        />
      </div>
    )
  }

  return (
    <button
      type="button"
      className={
        'mobile-drawer__thread-row' +
        (active ? ' mobile-drawer__thread-row--active' : '') +
        (unread ? ' mobile-drawer__thread-row--unread' : '')
      }
      onClick={onTap}
      onTouchStart={onLongPressStart}
      onTouchEnd={onLongPressEnd}
      onTouchMove={onLongPressEnd}
      onTouchCancel={onLongPressEnd}
      onPointerDown={onLongPressStart}
      onPointerUp={onLongPressEnd}
      onPointerLeave={onLongPressEnd}
      onPointerCancel={onLongPressEnd}
    >
      <MobileStatusDot busy={busy} unread={unread} />
      <span className="mobile-drawer__thread-title">
        {thread.title || 'untitled'}
      </span>
    </button>
  )
}
